import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, Outlet } from 'react-router-dom';
// Clerk auth disabled — using Supabase Auth via RequireAuth wrapper
import AcceptInvite from './pages/auth/AcceptInvite';
import TrustCenter from './pages/trust/TrustCenter';
import TrustDocument from './pages/trust/TrustDocument';
import SpaceInviteAccept from './pages/auth/SpaceInviteAccept';
import Login from './pages/auth/Login';
import ForgotPassword from './pages/auth/ForgotPassword';
import ResetPassword from './pages/auth/ResetPassword';
import ProductPortal from './pages/auth/ProductPortal';
import { RequireAuth } from './components/auth/RequireAuth';
import { ReportSnapshotPage } from './pages/prioritization/reports/ReportSnapshotPage';
import { DeprecatedBanner } from './components/DeprecatedBanner';
import { LegacyLook } from './components/LegacyLook';
import { RequireElevationAccess } from './components/auth/RequireElevationAccess';
import {
  elevationLevelForPath,
  hasElevationLevel,
  isTeamHubSubpage,
  useElevationAccess,
  useIsElevationOwner,
} from './lib/elevationAccess';
import { TeamHomeBar } from './components/TeamHomeBar';
import { TeamHome } from './pages/TeamHome';
import { DesignLanguageShell } from './features/design-language/DesignLanguageShell';
import { isDesignLanguagePath } from './features/design-language/designLanguagePaths';
import { RequireDeploymentSurface } from './components/auth/RequireDeploymentSurface';
import { Navigation } from './components/Navigation';
import { Footer } from './components/Footer';
import { AchievementNotificationManager } from './components/AchievementNotification';
import { ElevationSearch } from './components/ElevationSearch';
import { OverlayWizard } from './components/OverlayWizard';
import { TeachingWizard } from './components/TeachingWizard';
import { ProductTour } from './components/ProductTour';
import { ScrollToTop } from './components/ScrollToTop';
import { SentryContextSync } from './components/SentryContextSync';
import { AuthTelemetryTracker } from './components/AuthTelemetryTracker';
import { AuthSessionSync } from './components/auth/AuthSessionSync';
import HomeGate from './features/home/HomeGate';
import { WizardProvider } from './contexts/WizardContext';
import { useSupabaseSync } from './hooks/useSupabaseSync';
import { Engines } from './pages/Engines';
import { NotionHighlights } from './pages/NotionHighlights';
import { NotionIntake } from './pages/NotionIntake';
import { NotionIntakeRules } from './pages/NotionIntakeRules';
import { Operations } from './pages/Operations';
import { BatchImageProcessor } from './pages/BatchImageProcessor';
import { Architecture } from './pages/Architecture';
import { Studio } from './pages/Studio';
import { AdvancedSearch } from './pages/AdvancedSearch';
import { AINavigatorPage } from './pages/AINavigator';
import { ProgressPage } from './pages/Progress';
import { Lab } from './pages/Lab';
import SecurityPosture from './pages/SecurityPosture';
import GoalVerification from './pages/GoalVerification';
import { OutcomeDriverGenerator } from './pages/outcome-driver-generator/OutcomeDriverGenerator';
import PrioritizationToolSelector from './pages/PrioritizationToolSelector';
import PairwiseRankingElo from './pages/pairwise-ranking-elo/PairwiseRanking';
import PairwiseRanking from './pages/pairwise-ranking/PairwiseRanking';
import DotVoting from './pages/dot-voting/DotVoting';
import BuyAFeature from './pages/buy-a-feature/BuyAFeature';
import StackRanking from './pages/stack-ranking/StackRanking';
import PruneProductTree from './pages/prune-product-tree/PruneProductTree';
import SpeedBoat from './pages/speed-boat/SpeedBoat';
import SimpleWeightedScoring from './pages/simple-weighted-scoring/SimpleWeightedScoring';
import { ToastProvider } from './components/ui/Toast';
import RiceScoring from './pages/rice-scoring/RiceScoring';
import MoscowMethod from './pages/moscow-method/MoscowMethod';
import ParetoAnalysis from './pages/pareto-analysis/ParetoAnalysis';
import FastVsRight from './pages/fast-vs-right/FastVsRight';
import EisenhowerMatrix from './pages/eisenhower-matrix/EisenhowerMatrix';
import ImpactEffortMatrix from './pages/impact-effort-matrix/ImpactEffortMatrix';
import WeightedSmallestJobFirst from './pages/wsjf/WeightedSmallestJobFirst';
import CostOfDelay from './pages/cost-of-delay/CostOfDelay';
import MaxPrioritiesPyramid from './pages/max-priorities-pyramid/MaxPrioritiesPyramid';
import MagicPrioritization from './pages/magic-prioritization/MagicPrioritization';
import { GroupSessionPage } from './components/group/GroupSessionPage';
import { GroupJoinPage } from './components/group/GroupJoinPage';
import { ClarityCompass } from './pages/ClarityCompass';
import { FolderCreator } from './pages/FolderCreator';
import { YouTubeSearch } from './pages/YouTubeSearch';
import { DocumentationHub } from './pages/DocumentationHub';
import { Dashboard } from './pages/Dashboard';
import { LearningPathways } from './pages/LearningPathways';
import { RegistryEntry as RegistryEntryPage } from './pages/RegistryEntry';
import AppOrchestration from './pages/App';
import Workflow from './pages/Workflow';
import Gathering from './pages/workflow/Gathering';
import Tagging from './pages/workflow/Tagging';
import Formulating from './pages/workflow/Formulating';
import Creating from './pages/workflow/Creating';
import Applying from './pages/workflow/Applying';
import Publishing from './pages/workflow/Publishing';
import { ProductDesignDocsNav } from './components/ProductDesignDocsNav';
import { DesignLanguage } from './pages/DesignLanguage';
import { DesignLanguageLegacy } from './pages/DesignLanguageLegacy';
import { BrandDesignSystem } from './pages/BrandDesignSystem';
import { PrioritiesAIDesignLayout } from './pages/priorities-ai-design/PrioritiesAIDesignLayout';
import { PrioritiesAITokenSystemPage } from './pages/priorities-ai-design/PrioritiesAITokenSystemPage';
import { PrioritiesAIFoundationsPage } from './pages/priorities-ai-design/PrioritiesAIFoundationsPage';
import { PrioritiesAIIconLibraryPage } from './pages/priorities-ai-design/PrioritiesAIIconLibraryPage';
import { PrioritiesAILayoutGridPage } from './pages/priorities-ai-design/PrioritiesAILayoutGridPage';
import { PrioritiesAIDataVizPage } from './pages/priorities-ai-design/PrioritiesAIDataVizPage';
import { PrioritiesAIComponentCatalogPage } from './pages/priorities-ai-design/PrioritiesAIComponentCatalogPage';
import {
  PrioritiesAILogoSystemPage,
  PrioritiesAIBrandIdentityPage,
  PrioritiesAIAppIconsPage,
} from './pages/priorities-ai-design/PrioritiesAIBrandPages';
import { PrioritiesAIColorsPage } from './pages/priorities-ai-design/PrioritiesAIColorsPage';
import { PrioritiesAILegacyPage } from './pages/priorities-ai-design/PrioritiesAILegacyPage';
import { PrioritiesAISkillPage } from './pages/priorities-ai-design/PrioritiesAISkillPage';
import { PrioritiesAITypographyPage } from './pages/priorities-ai-design/PrioritiesAITypographyPage';
import {
  PrioritiesAIBrandElementsPage,
  PrioritiesAIComponentLibraryPage,
  PrioritiesAIVisualGrammarPage,
  PrioritiesAIPatternsPage,
  PrioritiesAIToneVoicePage,
  PrioritiesAIPrinciplesPage,
} from './pages/priorities-ai-design/PrioritiesAIDesignTopicPages';
import {
  isPrioritiesAiProductDesignPath,
} from './lib/prioritiesAiProductDesign';
import { LayoutGridSystem } from './pages/LayoutGridSystem';
import { MotionAnimationPrinciples } from './pages/MotionAnimationPrinciples';
import { ToneVoiceSystem } from './pages/ToneVoiceSystem';
import { PatternsBackgroundElements } from './pages/PatternsBackgroundElements';
import { InfographicDataVizStandards } from './pages/InfographicDataVizStandards';
import { ApplicationTemplates } from './pages/ApplicationTemplates';
import { BrandGuidelinesDocument } from './pages/BrandGuidelinesDocument';
import { AccessibilityRules } from './pages/AccessibilityRules';
import { BestPracticesDesignPrinciples } from './pages/BestPracticesDesignPrinciples';
import { VisualInspirationDirection } from './pages/VisualInspirationDirection';
import { VisualGrammarSystem } from './pages/VisualGrammarSystem';
import { LadderOfInferenceDemo } from './pages/LadderOfInferenceDemo';
import { VisualVerbalIntegration } from './pages/VisualVerbalIntegration';
import { IconLibrary } from './pages/IconLibrary';
import { BrandElements } from './pages/BrandElements';
import { ComponentLibrary } from './pages/ComponentLibrary';
import { IconCustomizer } from './pages/IconCustomizer';
import { TableDesigner } from './pages/TableDesigner';
import { VisualDesignGrammar } from './pages/VisualDesignGrammar';
import { LadderOfInferenceM2Model } from './pages/LadderOfInferenceM2Model';
import { LadderOfInferenceM1 } from './pages/LadderOfInferenceM1';
import { DesignOverview } from './pages/DesignOverview';
import { JohariWindowM2Model } from './pages/JohariWindowM2Model';
import { LogicalLevelsM2Model } from './pages/LogicalLevelsM2Model';
import { LadderOfInference } from './pages/LadderOfInference';
import { JohariWindow } from './pages/JohariWindow';
import { LogicalLevels } from './pages/LogicalLevels';
import { BoundedAgencyDelegation } from './pages/BoundedAgencyDelegation';
import { FieldOfPlay } from './pages/FieldOfPlay';
import { InquirySequence } from './pages/InquirySequence';
import { ArtifactGallery } from './pages/ArtifactGallery';
import { Glossary } from './pages/Glossary';
import MethodHome from './pages/method/MethodHome';
import MethodPhasePage from './pages/method/MethodPhasePage';
import ObjectTypesPage from './pages/method/ObjectTypesPage';
import ToolboxPage from './pages/method/ToolboxPage';
import MethodTour from './pages/method/MethodTour';
import { CanonEngineDefinition } from './pages/CanonEngineDefinition';
import { CanonEngineGuide } from './pages/CanonEngineGuide';
import OrchestrationEngine from './pages/OrchestrationEngine';
import { About } from './pages/About';
import { AboutArchived } from './pages/AboutArchived';
import { AboutArchivedSimple } from './pages/AboutArchivedSimple';
import { MissionControl } from './pages/MissionControl';
import { MissionControlArchived } from './pages/MissionControlArchived';
import { Information } from './pages/Information';
import { DesignSystemArchive } from './pages/DesignSystemArchive';
import { Roadmap, RoadmapOldAddressRedirect } from './pages/Roadmap';
// M3 to M0 moved to the Claritas app (Wes, 2026-10-18); their old addresses forward there.
import { MovedToClaritas } from './components/MovedToClaritas';
import { ComprehensiveVisualIdentitySystem } from './pages/ComprehensiveVisualIdentitySystem';
import { SourceOfTruthChecker } from './pages/SourceOfTruthChecker';
import { GeometryStudio } from './pages/GeometryStudio';
import { MentalMapLab } from './pages/mental-map/MentalMapLab';
import { MentalMapJoin } from './pages/mental-map/MentalMapJoin';
import { MentalModelPage } from './pages/mental-map/MentalModelPage';
import { MetaphorStudio } from './pages/MetaphorStudio';
import { BusinessApplications } from './pages/BusinessApplications';
import { Status } from './pages/Status';
import { SentenceBuilder } from './pages/SentenceBuilder';
import { ShapeCombinator } from './pages/ShapeCombinator';
import { SemanticModes } from './pages/SemanticModes';
import { GoldRushTemplate } from './pages/GoldRushTemplate';
import { SmartSuggestionsLab } from './pages/SmartSuggestionsLab';
import { RelationshipNetworkLab } from './pages/RelationshipNetworkLab';
import { ConnectionPathfinderLab } from './pages/ConnectionPathfinderLab';
import { TeamExplorationLab } from './pages/TeamExplorationLab';
import { MorningBootLab } from './features/morning-boot/MorningBootLab';
import { JudgmentIndexLab } from './features/judgment-index/JudgmentIndexLab';
import { ItemDetailSecondPassLab } from './features/item-detail-second-pass/ItemDetailSecondPassLab';
import { ItemDetailThirdPassLab } from './features/item-detail-third-pass/ItemDetailThirdPassLab';
import { CognitiveBiasLab } from './features/cognitive-bias/CognitiveBiasLab';
import { LexiconLab } from './features/lexicon/LexiconLab';
import { UnderstandingYourValues } from './pages/UnderstandingYourValues';
import { ChangingUnwantedBehaviors } from './pages/ChangingUnwantedBehaviors';
import { LogicalLevelsStory } from './pages/LogicalLevelsStory';
import { LevelAwareThinking } from './pages/LevelAwareThinking';
import { DelegationAuthorityLab } from './pages/DelegationAuthorityLab';
import { PartnerCapabilityApp } from './pages/PartnerCapabilityApp';
import { IslandExplorer } from './pages/IslandExplorer';
import { MountainAscent } from './pages/MountainAscent';
import { PeakWorld } from './pages/PeakWorld';
import { SiteMapPage } from './pages/SiteMap';
import { SiteMapAtAGlancePage } from './pages/SiteMapAtAGlance';
import { DocsDashboard } from './pages/DocsDashboard';
import { Archive } from './pages/Archive';
import { ArchiveOverview } from './pages/ArchiveOverview';
import { Workshops } from './pages/Workshops';
import { Analytics } from './pages/Analytics';
import { Outcomes } from './pages/Outcomes';
import { TeamCollaboration } from './pages/TeamCollaboration';
import { MyNotes } from './pages/MyNotes';
import { StakeholderIslandExplorer } from './pages/StakeholderIslandExplorer';
import { EnhancedFrameworkRouter } from './pages/EnhancedFrameworkRouter';
import { WesSandbox } from './pages/WesSandbox';
import Workbook from './pages/Workbook';
import WorkbookContent from './pages/WorkbookContent';
import WorkbookHome from './pages/workbook/WorkbookHome';
import DecidePhase from './pages/workbook/DecidePhase';
import EngagePhase from './pages/workbook/EngagePhase';
import GatherPhase from './pages/workbook/GatherPhase';
import ArrangePhase from './pages/workbook/ArrangePhase';
import PrioritizePhase from './pages/workbook/PrioritizePhase';
import CommitmentCheckPage from './pages/workbook/decide/CommitmentCheckPage';
import PriorityInventoryPage from './pages/workbook/decide/PriorityInventoryPage';
import FilterFocusPage from './pages/workbook/decide/FilterFocusPage';
import DecisionCriteriaPage from './pages/workbook/decide/DecisionCriteriaPage';
import PlayItOutDecidePage from './pages/workbook/decide/PlayItOutDecidePage';
import PriorityStatementPage from './pages/workbook/decide/PriorityStatementPage';
import FacilitationGuidePage from './pages/workbook/decide/FacilitationGuidePage';
import RetrievalCheckPage from './pages/workbook/decide/RetrievalCheckPage';
// Engage phase pages
import PurposeOutcomesPage from './pages/workbook/engage/PurposeOutcomesPage';
import StakeholderMapPage from './pages/workbook/engage/StakeholderMapPage';
import StakeholderWinsPage from './pages/workbook/engage/StakeholderWinsPage';
import ConstraintsRisksPage from './pages/workbook/engage/ConstraintsRisksPage';
import WaysOfWorkingPage from './pages/workbook/engage/WaysOfWorkingPage';
import PlayItOutEngagePage from './pages/workbook/engage/PlayItOutPage';
import EngagementBriefPage from './pages/workbook/engage/EngagementBriefPage';
import FacilitationRailsEngagePage from './pages/workbook/engage/FacilitationRailsPage';
// Gather phase pages
import OptionStormPage from './pages/workbook/gather/OptionStormPage';
import DIBBPage from './pages/workbook/gather/DIBBPage';
import MinimalMetadataPage from './pages/workbook/gather/MinimalMetadataPage';
import EvidenceCapsulePage from './pages/workbook/gather/EvidenceCapsulePage';
import DedupeMergePage from './pages/workbook/gather/DedupeMergePage';
import PlayItOutGatherPage from './pages/workbook/gather/PlayItOutGatherPage';
import BacklogExportPage from './pages/workbook/gather/BacklogExportPage';
import FacilitationRailsGatherPage from './pages/workbook/gather/FacilitationRailsGatherPage';
// Arrange phase pages
import AffinityClustersPage from './pages/workbook/arrange/AffinityClustersPage';
import ComparisonMethodPage from './pages/workbook/arrange/ComparisonMethodPage';
import EvidenceCheckPage from './pages/workbook/arrange/EvidenceCheckPage';
import PortfolioBalancePage from './pages/workbook/arrange/PortfolioBalancePage';
import DependenciesSequencingPage from './pages/workbook/arrange/DependenciesSequencingPage';
import RankedShortlistPage from './pages/workbook/arrange/RankedShortlistPage';
import PlayItOutArrangePage from './pages/workbook/arrange/PlayItOutArrangePage';
import FacilitationRailsArrangePage from './pages/workbook/arrange/FacilitationRailsArrangePage';
// Prioritize phase pages
import CriteriaGatePage from './pages/workbook/prioritize/CriteriaGatePage';
import CapacityWIPPage from './pages/workbook/prioritize/CapacityWIPPage';
import SequenceSlicePage from './pages/workbook/prioritize/SequenceSlicePage';
import MilestonesSignalsPage from './pages/workbook/prioritize/MilestonesSignalsPage';
import TradeoffsAntiGoalsPage from './pages/workbook/prioritize/TradeoffsAntiGoalsPage';
import NinetyDayPlanPage from './pages/workbook/prioritize/NinetyDayPlanPage';
import PlayItOutPrioritizePage from './pages/workbook/prioritize/PlayItOutPrioritizePage';
import SignoffRetrievalPage from './pages/workbook/prioritize/SignoffRetrievalPage';
// OQF Workbook imports
import OQFHome from './pages/workbook/oqf/OQFHome';
import Module1Page from './pages/workbook/oqf/Module1Page';
import Module2Page from './pages/workbook/oqf/Module2Page';
import Module3Page from './pages/workbook/oqf/Module3Page';
import Module4Page from './pages/workbook/oqf/Module4Page';
import Module5Page from './pages/workbook/oqf/Module5Page';
import Module6Page from './pages/workbook/oqf/Module6Page';
import Module7Page from './pages/workbook/oqf/Module7Page';
import Worksheet1_1Page from './pages/workbook/oqf/module1/Worksheet1_1Page';
import Worksheet1_2Page from './pages/workbook/oqf/module1/Worksheet1_2Page';
import Worksheet2_1Page from './pages/workbook/oqf/module2/Worksheet2_1Page';
import Worksheet4_1Page from './pages/workbook/oqf/module4/Worksheet4_1Page';
import Worksheet4_2Page from './pages/workbook/oqf/module4/Worksheet4_2Page';
import PrioritizationApp from './pages/prioritization/PrioritizationApp';
import BriefApp from './pages/brief/BriefApp';
import BriefSubmit from './pages/brief/BriefSubmit';
import BriefView from './pages/brief/BriefView';
import RecordAddressApp from './pages/record/RecordAddressApp';
import { LoopWorkspaceRedirect } from './pages/prioritization/catchball/LoopWorkspaceRedirect';
import { VisualLanguageStaticRedirect } from './components/VisualLanguageStaticRedirect';
import { PeakHomeStaticRedirect } from './components/PeakHomeStaticRedirect';
import PlanningApp from './pages/planning/PlanningApp';
import { ModuleRouteGuard } from './components/guards/ModuleRouteGuard';
import MandateApp from './pages/mandate/MandateApp';
import PlanSpineApp from './features/plan-spine/PlanSpineApp';
// App Layer — consumer-simple apps on the foundation (src/apps/registry.ts)
import AppsHome from './apps/AppsHome';
import AppCatalog from './apps/AppCatalog';
import PrioritizationLayout from './components/prioritization/Layout';
import StartApp from './apps/start/StartApp';
import SessionApp from './apps/session/SessionApp';
import GuestJoinScreen from './apps/session/GuestJoinScreen';
import SessionImportApp from './apps/session-import/SessionImportApp';
import ItemsApp from './apps/items/ItemsApp';
import ViabilityApp from './apps/viability/ViabilityApp';
import FocusApp from './apps/focus/FocusApp';
import IntakeApp from './apps/intake/IntakeApp';
import ReviewApp from './apps/review/ReviewApp';
import ClassificationApp from './apps/classification/ClassificationApp';
import RelationshipsApp from './apps/relationships/RelationshipsApp';
import AlignApp from './apps/align/AlignApp';
import AllocateApp from './apps/allocate/AllocateApp';
import RankApp from './apps/rank/RankApp';
import CutApp from './apps/cut/CutApp';
import BlueprintApp from './apps/blueprints/BlueprintApp';
import OffsiteApp from './apps/offsite/OffsiteApp';
import DecisionsApp from './apps/decisions/DecisionsApp';
import WhatsNextApp from './apps/whats-next/WhatsNextApp';
import First100DaysApp from './apps/first-100-days/First100DaysApp';
import { MandatePathGuard } from './components/mandate/MandatePathGuard';
import { PRIORITIZATION_PLAN_INSTRUMENT_PATH } from './lib/prioritizationPlanInstrumentPath';
import { MANDATE_BASE } from './lib/productSurfacePaths';
import ParticipantDashboard from './pages/participant/ParticipantDashboard';
import ParticipantSessionView from './pages/participant/ParticipantSessionView';
import PlatformAdminApp from './pages/super-admin';
import DemoMode from './pages/DemoMode';
import IndexConsistency from './pages/IndexConsistency';
import CanonEngine from './pages/CanonEngine';
import { journeyTracker } from './lib/journeyTracker';
import type { PageCategory } from './types/learning';
import { useTheme } from './contexts/ThemeContext';
import './utils/debugHelpers'; // Load debug helpers

/** Legacy /plan-of-record/* → canonical path under Prioritization. */
function RedirectLegacyStandalonePlanInstrument() {
  const { pathname } = useLocation();
  if (pathname === '/plan-of-record' || pathname === '/plan-of-record/') {
    return <Navigate to={PRIORITIZATION_PLAN_INSTRUMENT_PATH} replace />;
  }
  const tail = pathname.slice('/plan-of-record'.length);
  return <Navigate to={`${PRIORITIZATION_PLAN_INSTRUMENT_PATH}${tail}`} replace />;
}

/** Mandate moved to /apps/mandate (roadmap S-16, DEP-045): the old address opens the same page. */
function RedirectLegacyMandate() {
  const { pathname, search, hash } = useLocation();
  return <Navigate to={`${MANDATE_BASE}${pathname.slice('/mandate'.length)}${search}${hash}`} replace />;
}

// Supabase data sync initializer
function SupabaseSyncInitializer() {
  const syncStatus = useSupabaseSync();
  
  useEffect(() => {
    if (syncStatus.error) {
      console.warn('[App] Supabase sync error:', syncStatus.error);
    }
    if (syncStatus.isInitialized && syncStatus.supabaseAvailable) {
      console.log('[App] Supabase sync initialized');
    }
  }, [syncStatus]);
  
  // Show loading indicator if migrating
  if (syncStatus.isMigrating) {
    return (
      <div className="fixed inset-0 bg-white/80 z-[100] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-charcoal-600">Migrating data to cloud...</p>
        </div>
      </div>
    );
  }
  
  return null;
}

// Page tracking component
function PageTracker() {
  const location = useLocation();

  useEffect(() => {
    const getPageCategory = (path: string): PageCategory => {
      if (path === '/' || path === '/mission-control') return 'mission-control';
      if (path === '/overview') return 'mof-architecture';
      if (path === '/engines') return 'engines';
      if (path === '/grammar') return 'grammar';
      if (path === '/models') return 'models';
      if (path === '/templates') return 'templates';
      if (path === '/runtime') return 'runtime';
      if (path === '/island') return 'island';
      if (path === '/island/stakeholder-engagement') return 'island';
      if (path.startsWith('/enhanced/')) return 'entry-detail';
      if (path === '/mountain') return 'mountain';
      if (path === '/glossary') return 'glossary';
      if (path === '/advanced-search') return 'search';
      if (isPrioritiesAiProductDesignPath(path)) return 'design-language';
      if (path === '/design-language') return 'design-language';
      if (path === '/brand-design-system') return 'design-language';
      if (path === '/logo-system') return 'design-language';
      if (path === '/layout-grid-system') return 'design-language';
      if (path === '/motion-animation-principles') return 'design-language';
      if (path === '/tone-voice-system') return 'design-language';
      if (path === '/patterns-background-elements') return 'design-language';
      if (path === '/infographic-data-viz-standards') return 'design-language';
      if (path === '/application-templates') return 'design-language';
      if (path === '/lab/brand-guidelines-document') return 'design-language';
      if (path === '/favicon-app-icons') return 'design-language';
      if (path === '/accessibility-rules') return 'design-language';
      if (path === '/best-practices-design-principles') return 'design-language';
      if (path === '/visual-inspiration-direction') return 'design-language';
      if (path === '/visual-grammar-system') return 'design-language';
      if (path === '/ladder-of-inference-demo') return 'design-language';
      if (path === '/visual-verbal-integration') return 'design-language';
      if (path === '/icon-library') return 'design-language';
      if (path === '/brand-elements') return 'design-language';
      if (path === '/component-library') return 'design-language';
      if (path === '/component-library-v2') return 'design-language';
      if (path === '/icon-customizer') return 'design-language';
      if (path === '/visual-design-grammar') return 'design-language';
      if (path === '/grammar') return 'design-language';
      if (path === '/ladder-of-inference-m2-model') return 'design-language';
      if (path === '/johari-window-m2-model') return 'design-language';
      if (path === '/logical-levels-m2-model') return 'design-language';
      if (path === '/ladder-of-inference') return 'design-language';
      if (path === '/johari-window') return 'design-language';
      if (path === '/logical-levels') return 'design-language';
      if (path === '/bounded-agency-delegation') return 'design-language';
      if (path === '/field-of-play') return 'design-language';
      if (path === '/inquiry-sequence') return 'design-language';
      if (path === '/artifact-gallery') return 'design-language';
      if (path === '/visual-design-grammar') return 'design-language';
      if (path === '/workshops') return 'about'; // Reusing 'about' category for workshops
      if (path === '/outcomes') return 'about'; // Reusing 'about' category for outcomes
      if (path === '/team') return 'about'; // Reusing 'about' category for team
      if (path === '/analytics') return 'about'; // Reusing 'about' category for analytics
      if (path === '/my-notes') return 'about'; // Reusing 'about' category for my-notes
      if (path === '/about') return 'about';
      if (path.startsWith('/entry/')) return 'entry-detail';
      return 'mission-control';
    };

    // Wrap in try-catch to prevent errors from crashing the app
    try {
      journeyTracker.trackPageVisit(location.pathname, getPageCategory(location.pathname));
    } catch (error) {
      // Silently handle errors - journey tracking is non-critical
      console.warn('Failed to track page visit:', error);
    }
  }, [location]);

  return null;
}

/**
 * Returns true when the current path is an Elevation-gated route.
 * Only Elevation routes render the white Navigation header and Footer.
 * Auth pages, prioritization app, planning, sessions, standalone tools,
 * and the root redirect all suppress the Elevation chrome.
 */
const ELEVATION_PATH_PREFIX = '/elevation';

/**
 * Legacy flat prefixes (pre-2026-09-01). Kept PERMANENTLY for redirects:
 * these paths are persisted in the wild (notification action_urls, bookmarks,
 * shared links) — same contract as LegacyCatchballRedirect. Do not delete.
 */
const LEGACY_ELEVATION_PREFIXES = ['/home', '/design', '/app', '/apps/partner-capability', '/about', '/roadmap', '/site-map', '/site-map/at-a-glance', '/wes-sandbox', '/workbook', '/method', '/workflow', '/architecture', '/archive', '/docs', '/documentation', '/documentation-hub', '/comprehensive-visual-identity-system', '/lab', '/engines', '/notion-highlights', '/notion-intake', '/operations', '/batch-process-images', '/status', '/studio', '/ai-navigator', '/progress', '/advanced-search', '/grammar', '/models', '/templates', '/runtime', '/canon-engine', '/orchestration-engine', '/entry/', '/enhanced/', '/glossary', '/definition/', '/design-language', '/brand-design-system', '/priorities-ai-design-language', '/colors', '/typography', '/logo-system', '/layout-grid-system', '/motion-animation-principles', '/tone-voice-system', '/patterns-background-elements', '/infographic-data-viz-standards', '/application-templates', '/favicon-app-icons', '/accessibility-rules', '/best-practices-design-principles', '/visual-inspiration-direction', '/visual-grammar-system', '/visual-verbal-integration', '/visual-design-grammar', '/icon-library', '/brand-elements', '/component-library', '/icon-customizer', '/table-designer', '/artifact-gallery', '/ladder-of-inference', '/johari-window', '/logical-levels', '/bounded-agency-delegation', '/field-of-play', '/inquiry-sequence', '/island', '/mountain', '/workshops', '/outcomes', '/team', '/analytics', '/my-notes'];

function isElevationPath(pathname: string): boolean {
  // Design Language keeps its /elevation addresses but has its own shell.
  if (isDesignLanguagePath(pathname)) return false;
  return (
    pathname === ELEVATION_PATH_PREFIX ||
    pathname.startsWith(ELEVATION_PATH_PREFIX + '/') ||
    // Team pages with a product address under the hub (/home/roadmap) are
    // behind the same gate and keep the team bar and frame they had there.
    isTeamHubSubpage(pathname)
  );
}

/** True when a pathname uses a pre-/elevation flat URL that must redirect. */
function isLegacyElevationPath(pathname: string): boolean {
  if (pathname === '/apps' || pathname.startsWith('/apps/')) {
    return pathname.startsWith('/apps/partner-capability');
  }
  return LEGACY_ELEVATION_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(prefix)
  );
}

/**
 * Permanent redirect for legacy flat Elevation URLs → /elevation/… .
 * Mounted on the main catch-all; preserves subpath, query, and hash.
 */
function LegacyElevationRedirect() {
  const location = useLocation();
  if (isLegacyElevationPath(location.pathname)) {
    return (
      <Navigate
        to={`/elevation${location.pathname}${location.search}${location.hash}`}
        replace
      />
    );
  }
  return null;
}

/** Enforces the correct light/dark state on any Elevation path.
 *  When elevationTheme === 'light' it forces the global theme to light so the app
 *  shell and CSS variables stay in light mode. When elevationTheme === 'dark' it
 *  releases the forced-light lock so AppElevationBody's .dark wrapper div can
 *  re-assert dark CSS tokens via the .dark{} block in index.css. */
function ElevationThemeEnforcer({ elevationTheme }: { elevationTheme: 'light' | 'dark' }) {
  const location = useLocation();
  const { setForcedLight } = useTheme();

  useEffect(() => {
    // Design Language sets its own theme (features/design-language).
    if (isDesignLanguagePath(location.pathname)) return;
    if (!isElevationPath(location.pathname)) {
      setForcedLight(false);
      return;
    }
    // v3: product-canon pages are daylight editorial like the rest of
    // Elevation — no dark exception; the user's theme toggle governs.
    setForcedLight(elevationTheme === 'light');
    return () => setForcedLight(false);
  }, [location.pathname, elevationTheme]); // eslint-disable-line react-hooks/exhaustive-deps

  return null;
}

function ConditionalNav({
  onSearchClick,
  mode,
  onModeChange,
  elevationTheme,
  onElevationThemeChange,
}: {
  onSearchClick: () => void;
  mode: 'design' | 'runtime';
  onModeChange: (m: 'design' | 'runtime') => void;
  elevationTheme: 'light' | 'dark';
  onElevationThemeChange: (t: 'light' | 'dark') => void;
}) {
  const location = useLocation();
  const access = useElevationAccess();
  if (!isElevationPath(location.pathname)) return null;
  // Three levels (lib/elevationAccess.ts). Nothing is drawn for anyone below
  // the page's level, or before it is known who is signed in, so no menu
  // flashes before a redirect. The Elevation header is a menu of the owner's
  // own pages: it appears on those. Team pages get the way back to /home.
  if (access === 'pending') return null;
  const level = elevationLevelForPath(location.pathname);
  if (!hasElevationLevel(access, level)) return null;
  if (level === 'team')
    return (
      <div className="sticky top-0 z-50">
        <TeamHomeBar showElevation={access === 'owner'} />
      </div>
    );
  if (isPrioritiesAiProductDesignPath(location.pathname))
    return (
      <div className="sticky top-0 z-50 bg-[var(--bg)]">
        <ProductDesignDocsNav />
      </div>
    );
  return (
    <div className={`sticky top-0 z-50 ${elevationTheme === 'dark' ? 'bg-[#0D1117]' : 'bg-white'}`}>
      <Navigation
        onSearchClick={onSearchClick}
        mode={mode}
        onModeChange={onModeChange}
        elevationTheme={elevationTheme}
        onElevationThemeChange={onElevationThemeChange}
      />
    </div>
  );
}

function ConditionalMain({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  if (!isElevationPath(location.pathname)) return <>{children}</>;
  if (isPrioritiesAiProductDesignPath(location.pathname))
    return <main className="w-full flex-1 flex flex-col">{children}</main>;
  return <main className="container mx-auto px-4 py-8 flex-1">{children}</main>;
}

function ConditionalFooter() {
  const location = useLocation();
  const elevationOwner = useIsElevationOwner();
  if (!isElevationPath(location.pathname)) return null;
  // The Elevation footer links to the owner's own pages (Wes's Sandbox among
  // them), so like the header it appears only on owner pages, for the owner.
  if (!elevationOwner || elevationLevelForPath(location.pathname) !== 'owner') return null;
  if (isPrioritiesAiProductDesignPath(location.pathname))
    return (
      <footer className="daylight mt-auto bg-[var(--p-paper)] border-t border-[var(--p-hairline)] px-6 py-6">
        <p className="text-center font-mono text-[10px] tracking-[0.22em] text-[var(--text-ter)]">
          PRIORITIES.AI PRODUCT DESIGN CANON
        </p>
      </footer>
    );
  return <Footer />;
}

function AppElevationBody({ children, elevationTheme }: { children: React.ReactNode; elevationTheme: 'light' | 'dark' }) {
  const location = useLocation();
  const isElevation = isElevationPath(location.pathname);
  // v3: the product-canon pages are light-first daylight editorial (PR #520) —
  // they no longer pin .dark; only the user's Elevation theme toggle darkens.
  const useDark = isElevation && elevationTheme === 'dark';
  return (
    <div className={`daylight min-h-screen flex flex-col ${useDark ? 'dark bg-[var(--bg)]' : 'bg-[var(--p-paper)]'}`}>
      {children}
    </div>
  );
}

function App() {
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isTeachingWizardOpen, setIsTeachingWizardOpen] = useState(false);
  const [isProductTourOpen, setIsProductTourOpen] = useState(false);
  const [mode, setMode] = useState<'design' | 'runtime'>('design');
  const [elevationTheme, setElevationThemeState] = useState<'light' | 'dark'>(
    () => (localStorage.getItem('elevation-theme') as 'light' | 'dark') || 'light'
  );
  const setElevationTheme = (t: 'light' | 'dark') => {
    localStorage.setItem('elevation-theme', t);
    setElevationThemeState(t);
  };

  const elevationOwner = useIsElevationOwner();

  // Listen for teaching wizard trigger
  useEffect(() => {
    const handleTeachingWizardTrigger = () => {
      setIsTeachingWizardOpen(true);
    };

    const handleProductTourTrigger = () => {
      // Defer one frame so the Lab index click cannot hit the tour backdrop.
      requestAnimationFrame(() => setIsProductTourOpen(true));
    };


    window.addEventListener('triggerTeachingWizard', handleTeachingWizardTrigger);
    window.addEventListener('triggerProductTour', handleProductTourTrigger);
    return () => {
      window.removeEventListener('triggerTeachingWizard', handleTeachingWizardTrigger);
      window.removeEventListener('triggerProductTour', handleProductTourTrigger);
    };
  }, []);

  // CLERK DISABLED - bypassing authentication for now
  // const isInvitePath = window.location.pathname.startsWith('/invite/');

  return (
    <Router>
      <WizardProvider>
        <ScrollToTop />
        <SupabaseSyncInitializer />
        <SentryContextSync />
        <AuthTelemetryTracker />
        <AuthSessionSync />
        <PageTracker />
        <ElevationThemeEnforcer elevationTheme={elevationTheme} />
        {/* Isolated match for /demo only; * silences RR "no routes matched" for all other URLs */}
        <Routes>
          <Route path="/demo" element={<DemoMode />} />
          <Route path="*" element={null} />
        </Routes>
        <RequireAuth>
                <MandatePathGuard />
                <RequireDeploymentSurface />
                <AppElevationBody elevationTheme={elevationTheme}>
                  <ConditionalNav
                    onSearchClick={() => setIsSearchOpen(true)}
                    mode={mode}
                    onModeChange={setMode}
                    elevationTheme={elevationTheme}
                    onElevationThemeChange={setElevationTheme}
                  />
                  <ConditionalMain>
            <Routes>
            {/* ── Always-accessible (auth, prioritization, standalone tools) ───────── */}
            <Route path="/invite/:token" element={<AcceptInvite />} />
            <Route path="/invite/space/:token" element={<SpaceInviteAccept />} />
            {/* Public Trust Center (docs/trust/*) — no auth; for security/legal/procurement reviewers. */}
            <Route path="/trust" element={<TrustCenter />} />
            <Route path="/trust/:slug" element={<TrustDocument />} />
            {/* Decision Brief public routes — token-gated, no auth required */}
            <Route path="/brief/submit/:token" element={<BriefSubmit />} />
            <Route path="/brief/view/:token" element={<BriefView />} />
            {/* The Plan of Record's address — paper register; members under RLS; anyone else must be signed in AND hold a ?t= share token (mig 724: no anonymous read) */}
            <Route path="/record/:porId/*" element={<RecordAddressApp />} />
            {/* A report snapshot without the app chrome, for an iframe or a shared screen. Signed in only (owner ruling 2026-10-13). */}
            <Route path="/embed/reports/snapshots/:id" element={<ReportSnapshotPage embed />} />
            <Route path="/auth/login" element={<Login />} />
            <Route path="/auth/forgot-password" element={<ForgotPassword />} />
            <Route path="/auth/reset-password" element={<ResetPassword />} />
            <Route path="/auth/portal" element={<ProductPortal />} />
            {/* Static diagram engine — served from assets/visual-language/, not React */}
            <Route path="/visual-language" element={<VisualLanguageStaticRedirect />} />
            <Route path="/visual-language/*" element={<VisualLanguageStaticRedirect />} />
            <Route path="/peak-home" element={<PeakHomeStaticRedirect />} />
            <Route path="/peak-home/*" element={<PeakHomeStaticRedirect />} />
            {/* App home. A member whose experience profile is `guided`
                (migration 795) gets the home page bare; everyone else gets
                the same page inside the app shell on the web, or the App
                Layer launcher inside the native shells (Capacitor).
                RequireAuth still sends unauthenticated users to login either
                way. */}
            <Route path="/" element={<HomeGate />} />
            {/* The old address of the guided home. */}
            <Route path="/today" element={<Navigate to="/" replace />} />
            {/* Prioritization App */}
            <Route
              path="/prioritization/cascade/workbench/:cycleId/:loopId"
              element={<LoopWorkspaceRedirect />}
            />
            <Route path="/prioritization/*" element={<PrioritizationApp />} />
            {/* The participant surfaces mount ParticipantSessionFlow outside any Layout,
                and its "Leave session" button (ReverseActionButton) calls useToast, so
                without a provider /sessions/:sessionId crashed with "useToast must be
                used within a ToastProvider" (reproduced on staging 2026-10-15). */}
            <Route element={<ToastProvider><Outlet /></ToastProvider>}>
              <Route path="/sessions" element={<ParticipantDashboard />} />
              <Route path="/sessions/:sessionId" element={<ParticipantSessionView />} />
            </Route>
            {/* Group Prioritization — peer decision-making, no org/cycle required */}
            <Route path="/groups/join/:token" element={<GroupJoinPage />} />
            <Route path="/groups/:sessionId" element={<GroupSessionPage />} />
            {/* Decision Brief — standalone wedge product surface */}
            <Route path="/brief/*" element={<BriefApp />} />
            {/* Planning is a workspace module (build contract §2.7): off → its screens do not render. */}
            <Route
              path="/planning/*"
              element={(
                <ModuleRouteGuard moduleKey="planning" redirectTo="/prioritization">
                  <PlanningApp />
                </ModuleRouteGuard>
              )}
            />
            {/* Mandate is an app (roadmap S-16): one route family, gated in MandateApp. */}
            <Route path="/apps/mandate/*" element={<MandateApp />} />
            <Route path="/mandate/*" element={<RedirectLegacyMandate />} />
            {/* The Plan — chrome-free consumer surface answering the One
                Question for the signed-in user's workspace (product canon
                02B): what we're doing, the cut, what we're not doing, who
                ratified it. */}
            <Route path="/plan/*" element={<PlanSpineApp />} />
            {/* Mental Map room — PUBLIC by design: the link is the access, and a
                guest joins with a name only (migration 808). Chrome-free. */}
            <Route path="/map/:token" element={<MentalMapJoin />} />
            {/* App Layer — consumer-simple apps, one gated route family per app
                (module+flag pair per §2.6; registry: src/apps/registry.ts). */}
            {/* One ToastProvider for the whole /apps family: Hire, Rank, Session, Allocate,
                Offsite and the other authored apps call useToast, and none of them sits
                inside a Layout that provides one — /apps/hire crashed on "useToast must be
                used within a ToastProvider". */}
            <Route element={<ToastProvider><Outlet /></ToastProvider>}>
            {/* The launcher lives in the product shell: same header, same way home. */}
            <Route path="/apps" element={<PrioritizationLayout><AppsHome /></PrioritizationLayout>} />
            <Route path="/apps/catalog" element={<PrioritizationLayout><AppCatalog /></PrioritizationLayout>} />
            <Route path="/apps/start/*" element={<StartApp />} />
            {/* Guest participant join — PUBLIC and ungated by design.
                Mounted above the /apps/session/* family so it never enters
                RequireAppAccess: a guest has no workspace, module or
                entitlement to satisfy. Writes go through token-scoped
                SECURITY DEFINER RPCs (migration 660). */}
            <Route path="/apps/session/join/:token" element={<GuestJoinScreen />} />
            <Route path="/apps/session/*" element={<SessionApp />} />
            <Route path="/apps/rank/*" element={<RankApp />} />
            <Route path="/apps/cut/*" element={<CutApp />} />
            <Route path="/apps/session-import/*" element={<SessionImportApp />} />
            <Route path="/apps/items/*" element={<ItemsApp />} />
            <Route path="/apps/viability/*" element={<ViabilityApp />} />
            <Route path="/apps/focus/*" element={<FocusApp />} />
            <Route path="/apps/intake/*" element={<IntakeApp />} />
            <Route path="/apps/review/*" element={<ReviewApp />} />
            <Route path="/apps/classification/*" element={<ClassificationApp />} />
            <Route path="/apps/relationships/*" element={<RelationshipsApp />} />
            <Route path="/apps/align/*" element={<AlignApp />} />
            <Route path="/apps/allocate/*" element={<AllocateApp />} />
            {/* Authored tail — one renderer, one blueprint record each. */}
            <Route path="/apps/hire/*" element={<BlueprintApp appId="hire" />} />
            <Route path="/apps/vendor-select/*" element={<BlueprintApp appId="vendor-select" />} />
            <Route path="/apps/risk-triage/*" element={<BlueprintApp appId="risk-triage" />} />
            <Route path="/apps/post-mortem/*" element={<BlueprintApp appId="post-mortem" />} />
            <Route path="/apps/ask-the-room/*" element={<BlueprintApp appId="ask-the-room" />} />
            <Route path="/apps/offsite/*" element={<OffsiteApp />} />
            <Route path="/apps/decisions/*" element={<DecisionsApp />} />
            <Route path="/apps/whats-next/*" element={<WhatsNextApp />} />
            <Route path="/apps/first-100-days/*" element={<First100DaysApp />} />
            </Route>
            <Route path="/plan-of-record/*" element={<RedirectLegacyStandalonePlanInstrument />} />
            <Route path="/platform-admin/*" element={<PlatformAdminApp />} />
            {/* Standalone prioritization tools — also embedded within /prioritization, never gated.
                Wrapped in ToastProvider: inside /prioritization these tools get one from
                Layout, but on their own routes they had none, so the first child to call
                useToast threw "useToast must be used within a ToastProvider" and took the
                page down. Reproduced on /simple-weighted-scoring → Configure Session. */}
            <Route element={<ToastProvider><Outlet /></ToastProvider>}>
            <Route path="/pairwise-ranking" element={<PairwiseRanking />} />
            <Route path="/pairwise-ranking-elo" element={<PairwiseRankingElo />} />
            <Route path="/dot-voting" element={<DotVoting />} />
            <Route path="/buy-a-feature" element={<BuyAFeature />} />
            <Route path="/stack-ranking" element={<StackRanking />} />
            <Route path="/ahp" element={<Navigate to="/prioritization/tools" replace />} />
            <Route path="/prune-product-tree" element={<PruneProductTree />} />
            <Route path="/speed-boat" element={<SpeedBoat />} />
            <Route path="/simple-weighted-scoring" element={<SimpleWeightedScoring />} />
            <Route path="/rice-scoring" element={<RiceScoring />} />
            <Route path="/moscow-method" element={<MoscowMethod />} />
            <Route path="/pareto-analysis" element={<ParetoAnalysis />} />
            <Route path="/fast-vs-right" element={<FastVsRight />} />
            <Route path="/impact-effort-matrix" element={<ImpactEffortMatrix />} />
            <Route path="/eisenhower-matrix" element={<EisenhowerMatrix />} />
            <Route path="/wsjf" element={<WeightedSmallestJobFirst />} />
            <Route path="/cost-of-delay" element={<CostOfDelay />} />
            <Route path="/max-priorities-pyramid" element={<MaxPrioritiesPyramid />} />
            <Route path="/magic-prioritization" element={<MagicPrioritization />} />
            </Route>
            {/* Legacy/alias redirects — these don't point to Elevation pages directly */}
            {/* Old names for Mission Control. /home is now the team hub, so these go
                straight to Mission Control, which is the owner's (lib/elevationAccess.ts). */}
            <Route path="/overview" element={<Navigate to="/elevation/home" replace />} />
            <Route path="/mission-control" element={<Navigate to="/elevation/home" replace />} />
            <Route path="/mof" element={<Navigate to="/" replace />} />
            <Route path="/m4-engines" element={<Navigate to="/engines" replace />} />
            <Route path="/meta-stack" element={<Navigate to="/grammar" replace />} />
            <Route path="/m2-models" element={<Navigate to="/models" replace />} />
            <Route path="/explorer" element={<Navigate to="/models" replace />} />
            <Route path="/m1-templates" element={<Navigate to="/templates" replace />} />
            <Route path="/m0-instances" element={<Navigate to="/runtime" replace />} />
            {/* ── Elevation-gated routes ─────────────────────────────────────────────
                Three levels (lib/elevationAccess.ts, Wes 2026-10-18): the owner's
                own pages (ELEVATION_OWNER_ONLY_PATHS) open for the owner alone;
                everything else here, the /home hub included, opens for the team;
                customers open nothing. For whoever is let in, the
                `elevation_platform` workspace module AND the `elevation_nav`
                individual feature flag must also be enabled. Blocked users are
                sent to /prioritization/dashboard.                              ──── */}
            <Route element={<RequireElevationAccess />}>
              {/* Team hub (Proposal 09 § 8.2). Mission Control is /elevation/home. */}
              <Route path="/home" element={<TeamHome />} />
              {/* The product roadmap's address (Proposal 09 § 8.2, Product records) */}
              <Route path="/home/roadmap" element={<Roadmap />} />
              {/* Hub */}
              <Route path="/elevation/home" element={<MissionControl />} />
              <Route path="/elevation/design" element={<DesignOverview />} />
              <Route path="/elevation/app" element={<AppOrchestration />} />
              <Route path="/elevation/apps/partner-capability" element={<PartnerCapabilityApp />} />
              <Route path="/elevation/about" element={<About />} />
              <Route path="/elevation/roadmap" element={<RoadmapOldAddressRedirect />} />
              <Route path="/elevation/site-map" element={<SiteMapPage />} />
              <Route path="/elevation/site-map/at-a-glance" element={<SiteMapAtAGlancePage />} />
              <Route path="/elevation/wes-sandbox" element={<WesSandbox />} />
              {/* DEGAP Workbook */}
              <Route path="/elevation/workbook" element={<Workbook />} />
              <Route path="/elevation/workbook/home" element={<WorkbookHome />} />
              <Route path="/elevation/workbook/decide" element={<DecidePhase />} />
            <Route path="/elevation/workbook/decide/page-1" element={<CommitmentCheckPage />} />
            <Route path="/elevation/workbook/decide/page-2" element={<PriorityInventoryPage />} />
            <Route path="/elevation/workbook/decide/page-3" element={<FilterFocusPage />} />
            <Route path="/elevation/workbook/decide/page-4" element={<DecisionCriteriaPage />} />
            <Route path="/elevation/workbook/decide/page-5" element={<PlayItOutDecidePage />} />
            <Route path="/elevation/workbook/decide/page-6" element={<PriorityStatementPage />} />
            <Route path="/elevation/workbook/decide/page-7" element={<FacilitationGuidePage />} />
            <Route path="/elevation/workbook/decide/page-8" element={<RetrievalCheckPage />} />
              <Route path="/elevation/workbook/engage" element={<EngagePhase />} />
              <Route path="/elevation/workbook/gather" element={<GatherPhase />} />
              <Route path="/elevation/workbook/arrange" element={<ArrangePhase />} />
              <Route path="/elevation/workbook/prioritize" element={<PrioritizePhase />} />
              {/* Engage phase pages */}
              <Route path="/elevation/workbook/engage/page-1" element={<PurposeOutcomesPage />} />
              <Route path="/elevation/workbook/engage/page-2" element={<StakeholderMapPage />} />
              <Route path="/elevation/workbook/engage/page-3" element={<StakeholderWinsPage />} />
              <Route path="/elevation/workbook/engage/page-4" element={<ConstraintsRisksPage />} />
              <Route path="/elevation/workbook/engage/page-5" element={<WaysOfWorkingPage />} />
              <Route path="/elevation/workbook/engage/page-6" element={<PlayItOutEngagePage />} />
              <Route path="/elevation/workbook/engage/page-7" element={<EngagementBriefPage />} />
              <Route path="/elevation/workbook/engage/page-8" element={<FacilitationRailsEngagePage />} />
              {/* Gather phase pages */}
              <Route path="/elevation/workbook/gather/page-1" element={<OptionStormPage />} />
              <Route path="/elevation/workbook/gather/page-2" element={<DIBBPage />} />
              <Route path="/elevation/workbook/gather/page-3" element={<MinimalMetadataPage />} />
              <Route path="/elevation/workbook/gather/page-4" element={<EvidenceCapsulePage />} />
              <Route path="/elevation/workbook/gather/page-5" element={<DedupeMergePage />} />
              <Route path="/elevation/workbook/gather/page-6" element={<PlayItOutGatherPage />} />
              <Route path="/elevation/workbook/gather/page-7" element={<BacklogExportPage />} />
              <Route path="/elevation/workbook/gather/page-8" element={<FacilitationRailsGatherPage />} />
              {/* Arrange phase pages */}
              <Route path="/elevation/workbook/arrange/page-1" element={<AffinityClustersPage />} />
              <Route path="/elevation/workbook/arrange/page-2" element={<ComparisonMethodPage />} />
              <Route path="/elevation/workbook/arrange/page-3" element={<EvidenceCheckPage />} />
              <Route path="/elevation/workbook/arrange/page-4" element={<PortfolioBalancePage />} />
              <Route path="/elevation/workbook/arrange/page-5" element={<DependenciesSequencingPage />} />
              <Route path="/elevation/workbook/arrange/page-6" element={<RankedShortlistPage />} />
              <Route path="/elevation/workbook/arrange/page-7" element={<PlayItOutArrangePage />} />
              <Route path="/elevation/workbook/arrange/page-8" element={<FacilitationRailsArrangePage />} />
              {/* Prioritize phase pages */}
              <Route path="/elevation/workbook/prioritize/page-1" element={<CriteriaGatePage />} />
              <Route path="/elevation/workbook/prioritize/page-2" element={<CapacityWIPPage />} />
              <Route path="/elevation/workbook/prioritize/page-3" element={<SequenceSlicePage />} />
              <Route path="/elevation/workbook/prioritize/page-4" element={<MilestonesSignalsPage />} />
              <Route path="/elevation/workbook/prioritize/page-5" element={<TradeoffsAntiGoalsPage />} />
              <Route path="/elevation/workbook/prioritize/page-6" element={<NinetyDayPlanPage />} />
              <Route path="/elevation/workbook/prioritize/page-7" element={<PlayItOutPrioritizePage />} />
              <Route path="/elevation/workbook/prioritize/page-8" element={<SignoffRetrievalPage />} />
              {/* OQF Workbook routes */}
              <Route path="/elevation/workbook/oqf" element={<OQFHome />} />
              <Route path="/elevation/workbook/oqf/module-1" element={<Module1Page />} />
              <Route path="/elevation/workbook/oqf/module-1/worksheet-1-1" element={<Worksheet1_1Page />} />
              <Route path="/elevation/workbook/oqf/module-1/worksheet-1-2" element={<Worksheet1_2Page />} />
              <Route path="/elevation/workbook/oqf/module-2" element={<Module2Page />} />
              <Route path="/elevation/workbook/oqf/module-2/worksheet-2-1" element={<Worksheet2_1Page />} />
              <Route path="/elevation/workbook/oqf/module-3" element={<Module3Page />} />
              <Route path="/elevation/workbook/oqf/module-4" element={<Module4Page />} />
              <Route path="/elevation/workbook/oqf/module-4/worksheet-4-1" element={<Worksheet4_1Page />} />
              <Route path="/elevation/workbook/oqf/module-4/worksheet-4-2" element={<Worksheet4_2Page />} />
              <Route path="/elevation/workbook/oqf/module-5" element={<Module5Page />} />
              <Route path="/elevation/workbook/oqf/module-6" element={<Module6Page />} />
              <Route path="/elevation/workbook/oqf/module-7" element={<Module7Page />} />
              <Route path="/elevation/workbook/:category/:filename" element={<WorkbookContent />} />
              {/* The Method — book-to-app connective layer (DEGAP phases, object types, toolbox) */}
              <Route path="/elevation/method" element={<MethodHome />} />
              <Route path="/elevation/method/tour" element={<MethodTour />} />
              <Route path="/elevation/method/object-types" element={<ObjectTypesPage />} />
              <Route path="/elevation/method/toolbox" element={<ToolboxPage />} />
              <Route path="/elevation/method/:phase" element={<MethodPhasePage />} />
              {/* Workflow */}
              <Route path="/elevation/workflow" element={<Workflow />} />
              <Route path="/elevation/workflow/gathering" element={<Gathering />} />
              <Route path="/elevation/workflow/tagging" element={<Tagging />} />
              <Route path="/elevation/workflow/formulating" element={<Formulating />} />
              <Route path="/elevation/workflow/creating" element={<Creating />} />
              <Route path="/elevation/workflow/applying" element={<Applying />} />
              <Route path="/elevation/workflow/publishing" element={<Publishing />} />
              {/* Architecture & Archive */}
              <Route path="/elevation/architecture" element={<Architecture />} />
              <Route path="/elevation/archive" element={<LegacyLook><Archive /></LegacyLook>} />
              <Route path="/elevation/archive/overview" element={<LegacyLook><ArchiveOverview /></LegacyLook>} />
              <Route path="/elevation/archive/information" element={<LegacyLook><Information /></LegacyLook>} />
              <Route path="/elevation/archive/design-system" element={<LegacyLook><DesignSystemArchive /></LegacyLook>} />
              <Route path="/elevation/archive/home" element={<LegacyLook><AboutArchived /></LegacyLook>} />
              <Route path="/elevation/archive/home-simple" element={<LegacyLook><AboutArchivedSimple /></LegacyLook>} />
              <Route path="/elevation/archive/mission-control" element={<LegacyLook><MissionControlArchived /></LegacyLook>} />
              {/* Documentation */}
              <Route path="/elevation/docs" element={<DocsDashboard />} />
              <Route path="/elevation/documentation" element={<Navigate to="/documentation-hub" replace />} />
              <Route path="/elevation/documentation-hub" element={<DocumentationHub />} />
              {/* Lab */}
              <Route path="/elevation/lab" element={<Lab />} />
              <Route path="/elevation/lab/outcome-driver-generator" element={<OutcomeDriverGenerator />} />
              <Route path="/elevation/lab/canon" element={<Navigate to="/canon-engine" replace />} />
              <Route path="/elevation/lab/architecture" element={<Architecture />} />
              <Route path="/elevation/lab/docs" element={<DocsDashboard />} />
              <Route path="/elevation/lab/dashboard" element={<Dashboard />} />
              <Route path="/elevation/lab/consistency" element={<IndexConsistency />} />
              <Route path="/elevation/lab/learning-pathways" element={<LearningPathways />} />
              <Route path="/elevation/lab/tool-selector/*" element={<PrioritizationToolSelector />} />
              <Route path="/elevation/lab/clarity-compass" element={<ClarityCompass />} />
              <Route path="/elevation/lab/folder-creator" element={<FolderCreator />} />
              <Route path="/elevation/lab/youtube-search" element={<YouTubeSearch />} />
              <Route path="/elevation/lab/sentence-builder" element={<SentenceBuilder />} />
              <Route path="/elevation/lab/shape-combinator" element={<ShapeCombinator />} />
              <Route path="/elevation/lab/semantic-modes" element={<SemanticModes />} />
              <Route path="/elevation/lab/gold-rush-template" element={<GoldRushTemplate />} />
              <Route path="/elevation/lab/m2-model-taxonomy" element={<MovedToClaritas />} />
              <Route path="/elevation/lab/business-applications" element={<BusinessApplications />} />
              <Route path="/elevation/lab/mental-map" element={<MentalMapLab />} />
              <Route path="/elevation/lab/mental-map/model/:modelId" element={<MentalModelPage />} />
              <Route path="/elevation/lab/source-of-truth-checker" element={<SourceOfTruthChecker />} />
              <Route path="/elevation/lab/smart-suggestions" element={<SmartSuggestionsLab />} />
              <Route path="/elevation/lab/morning-boot" element={<MorningBootLab />} />
              <Route path="/elevation/lab/judgment-index" element={<JudgmentIndexLab />} />
              <Route path="/elevation/lab/item-detail-second-pass" element={<ItemDetailSecondPassLab />} />
              <Route path="/elevation/lab/item-detail-third-pass" element={<ItemDetailThirdPassLab />} />
              <Route path="/elevation/lab/cognitive-bias" element={<CognitiveBiasLab />} />
              <Route path="/elevation/lab/lexicon" element={<LexiconLab />} />
              <Route path="/elevation/lab/relationship-network" element={<RelationshipNetworkLab />} />
              <Route path="/elevation/lab/connection-pathfinder" element={<ConnectionPathfinderLab />} />
              <Route path="/elevation/lab/team-exploration" element={<TeamExplorationLab />} />
              {/* Retired for the What's Next app (roadmap S-16, DEP-044). */}
              <Route path="/elevation/lab/whats-next" element={<Navigate to="/apps/whats-next" replace />} />
              <Route path="/elevation/lab/understanding-your-values" element={<UnderstandingYourValues />} />
              <Route path="/elevation/lab/changing-unwanted-behaviors" element={<ChangingUnwantedBehaviors />} />
              <Route path="/elevation/lab/logical-levels-story" element={<LogicalLevelsStory />} />
              <Route path="/elevation/lab/level-aware-thinking" element={<LevelAwareThinking />} />
              <Route path="/elevation/lab/delegation-authority" element={<DelegationAuthorityLab />} />
              <Route path="/elevation/lab/prioritization-archive/security" element={<SecurityPosture />} />
              <Route path="/elevation/lab/prioritization-archive/goals" element={<GoalVerification />} />
              {/* Operations / Platform */}
              <Route path="/elevation/engines" element={<Engines />} />
              <Route path="/elevation/notion-highlights" element={<NotionHighlights />} />
              <Route path="/elevation/notion-intake" element={<NotionIntake />} />
              <Route path="/elevation/notion-intake/rules" element={<NotionIntakeRules />} />
              <Route path="/elevation/operations" element={<Operations />} />
              <Route path="/elevation/batch-process-images" element={<BatchImageProcessor />} />
              <Route path="/elevation/status" element={<Status />} />
              <Route path="/elevation/studio" element={<Studio />} />
              <Route path="/elevation/ai-navigator" element={<AINavigatorPage />} />
              <Route path="/elevation/progress" element={<ProgressPage />} />
              <Route path="/elevation/advanced-search" element={<AdvancedSearch />} />
              {/* Knowledge system */}
              <Route path="/elevation/grammar" element={<MovedToClaritas />} />
              <Route path="/elevation/models" element={<MovedToClaritas />} />
              <Route path="/elevation/templates" element={<MovedToClaritas />} />
              <Route path="/elevation/runtime" element={<MovedToClaritas />} />
              <Route path="/elevation/canon-engine" element={<CanonEngine />} />
              <Route path="/elevation/canon-engine/guide" element={<CanonEngineGuide />} />
              <Route path="/elevation/orchestration-engine" element={<OrchestrationEngine />} />
              <Route path="/elevation/entry/:id" element={<RegistryEntryPage />} />
              <Route path="/elevation/enhanced/:id" element={<EnhancedFrameworkRouter />} />
              <Route path="/elevation/glossary" element={<Glossary />} />
              <Route path="/elevation/definition/canon-engine" element={<CanonEngineDefinition />} />
              <Route path="/elevation/artifact-gallery" element={<ArtifactGallery />} />
              {/* Mental models */}
              <Route path="/elevation/ladder-of-inference" element={<LadderOfInference />} />
              <Route path="/elevation/ladder-of-inference-m1" element={<LadderOfInferenceM1 />} />
              <Route path="/elevation/ladder-of-inference-m2-model" element={<LadderOfInferenceM2Model />} />
              <Route path="/elevation/ladder-of-inference-demo" element={<LadderOfInferenceDemo />} />
              <Route path="/elevation/johari-window" element={<JohariWindow />} />
              <Route path="/elevation/johari-window-m2-model" element={<JohariWindowM2Model />} />
              <Route path="/elevation/logical-levels" element={<LogicalLevels />} />
              <Route path="/elevation/logical-levels-m2-model" element={<LogicalLevelsM2Model />} />
              <Route path="/elevation/bounded-agency-delegation" element={<BoundedAgencyDelegation />} />
              <Route path="/elevation/field-of-play" element={<FieldOfPlay />} />
              <Route path="/elevation/inquiry-sequence" element={<InquirySequence />} />
              {/* Exploration */}
              <Route path="/elevation/island" element={<IslandExplorer />} />
              <Route path="/elevation/island/stakeholder-engagement" element={<StakeholderIslandExplorer />} />
              <Route path="/elevation/mountain" element={<MountainAscent />} />
              {/* Unity WebGL world host — the real spatial surface (apps/mountain-world/) */}
              <Route path="/world" element={<PeakWorld />} />
              {/* Personal & Hub pages */}
              <Route path="/elevation/workshops" element={<Workshops />} />
              <Route path="/elevation/outcomes" element={<Outcomes />} />
              <Route path="/elevation/team" element={<TeamCollaboration />} />
              <Route path="/elevation/analytics" element={<Analytics />} />
              <Route path="/elevation/my-notes" element={<MyNotes />} />
            </Route> {/* end RequireElevationAccess */}
            {/* ── Design Language: its own shell ─────────────────────────────────────
                Team material that stays with Priorities.ai when Elevation leaves
                (Proposal 09 § 4 coupling 6, § 8.2). DesignLanguageShell is its gate (the
                team level in lib/elevationAccess.ts), header and theme; the Elevation
                chrome above steps aside on these addresses, which are unchanged
                (features/design-language/designLanguagePaths.ts).                 ──── */}
            <Route element={<DesignLanguageShell />}>
              <Route path="/elevation/design-language" element={<DesignLanguage />} />
              <Route path="/elevation/brand-design-system" element={<LegacyLook><DeprecatedBanner to="/elevation/design-language" label="The design language" /><BrandDesignSystem /></LegacyLook>} />
              <Route path="/elevation/priorities-ai-design-language" element={<PrioritiesAIDesignLayout />}>
                {/* The product hub was a second front door listing the same references; one door now. */}
                <Route index element={<Navigate to="/elevation/design-language" replace />} />
                <Route path="foundations" element={<PrioritiesAIFoundationsPage />} />
                <Route path="token-system" element={<PrioritiesAITokenSystemPage />} />
                <Route path="logo-system" element={<PrioritiesAILogoSystemPage />} />
                <Route path="brand-identity" element={<PrioritiesAIBrandIdentityPage />} />
                <Route path="app-icons" element={<PrioritiesAIAppIconsPage />} />
                <Route path="colors" element={<PrioritiesAIColorsPage />} />
                <Route path="skills/:skill/*" element={<PrioritiesAISkillPage />} />
                <Route path="legacy" element={<LegacyLook><DeprecatedBanner to="/elevation/priorities-ai-design-language/colors" label="Colors — Paper and Night" still="Parts of the app's code still name these colors, so they are not deleted yet. The app repaints them onto current tokens." /><PrioritiesAILegacyPage /></LegacyLook>} />
                <Route path="typography" element={<PrioritiesAITypographyPage />} />
                <Route path="icon-library" element={<PrioritiesAIIconLibraryPage />} />
                <Route path="brand-elements" element={<LegacyLook><DeprecatedBanner to="/elevation/design-language" label="The design language" /><PrioritiesAIBrandElementsPage /></LegacyLook>} />
                <Route path="component-library" element={<LegacyLook><DeprecatedBanner to="/elevation/design-system/components" label="Component catalog" /><PrioritiesAIComponentLibraryPage /></LegacyLook>} />
                <Route path="visual-design-grammar" element={<LegacyLook><DeprecatedBanner to="/elevation/design-language" label="The design language" /><PrioritiesAIVisualGrammarPage /></LegacyLook>} />
                <Route path="patterns-background-elements" element={<LegacyLook><DeprecatedBanner to="/elevation/design-language" label="The design language" /><PrioritiesAIPatternsPage /></LegacyLook>} />
                <Route path="infographic-data-viz-standards" element={<PrioritiesAIDataVizPage />} />
                <Route path="layout-grid-system" element={<PrioritiesAILayoutGridPage />} />
                <Route path="tone-voice-system" element={<LegacyLook><DeprecatedBanner to="/elevation/design-language" label="The design language" /><PrioritiesAIToneVoicePage /></LegacyLook>} />
                <Route path="best-practices-design-principles" element={<LegacyLook><DeprecatedBanner to="/elevation/design-language" label="The design language" /><PrioritiesAIPrinciplesPage /></LegacyLook>} />
              </Route>
              {/* Top-level (own theme root) so live component specimens can toggle light/dark */}
              <Route path="/elevation/design-system/components" element={<PrioritiesAIComponentCatalogPage />} />
              <Route path="/elevation/colors" element={<Navigate to="/priorities-ai-design-language/colors" replace />} />
              <Route path="/elevation/typography" element={<Navigate to="/priorities-ai-design-language/typography" replace />} />
              <Route path="/elevation/logo-system" element={<Navigate to="/priorities-ai-design-language/logo-system" replace />} />
              <Route path="/elevation/layout-grid-system" element={<LegacyLook><DeprecatedBanner to="/elevation/design-language" label="The design language" /><LayoutGridSystem /></LegacyLook>} />
              <Route path="/elevation/motion-animation-principles" element={<LegacyLook><DeprecatedBanner to="/elevation/design-language" label="The design language" /><MotionAnimationPrinciples /></LegacyLook>} />
              <Route path="/elevation/tone-voice-system" element={<LegacyLook><DeprecatedBanner to="/elevation/design-language" label="The design language" /><ToneVoiceSystem /></LegacyLook>} />
              <Route path="/elevation/patterns-background-elements" element={<LegacyLook><DeprecatedBanner to="/elevation/design-language" label="The design language" /><PatternsBackgroundElements /></LegacyLook>} />
              <Route path="/elevation/infographic-data-viz-standards" element={<LegacyLook><DeprecatedBanner to="/elevation/design-language" label="The design language" /><InfographicDataVizStandards /></LegacyLook>} />
              <Route path="/elevation/application-templates" element={<LegacyLook><DeprecatedBanner to="/elevation/design-language" label="The design language" /><ApplicationTemplates /></LegacyLook>} />
              <Route path="/elevation/favicon-app-icons" element={<Navigate to="/priorities-ai-design-language/app-icons" replace />} />
              <Route path="/elevation/accessibility-rules" element={<LegacyLook><DeprecatedBanner to="/elevation/design-language" label="The design language" /><AccessibilityRules /></LegacyLook>} />
              <Route path="/elevation/best-practices-design-principles" element={<LegacyLook><DeprecatedBanner to="/elevation/design-language" label="The design language" /><BestPracticesDesignPrinciples /></LegacyLook>} />
              <Route path="/elevation/visual-inspiration-direction" element={<LegacyLook><DeprecatedBanner to="/elevation/design-language" label="The design language" /><VisualInspirationDirection /></LegacyLook>} />
              <Route path="/elevation/visual-grammar-system" element={<LegacyLook><DeprecatedBanner to="/visual-language/grammar-spec.html" label="Grammar specification" external /><VisualGrammarSystem /></LegacyLook>} />
              <Route path="/elevation/visual-verbal-integration" element={<LegacyLook><DeprecatedBanner to="/elevation/design-language" label="The design language" /><VisualVerbalIntegration /></LegacyLook>} />
              <Route path="/elevation/visual-design-grammar" element={<LegacyLook><DeprecatedBanner to="/visual-language/grammar-spec.html" label="Grammar specification" external /><VisualDesignGrammar /></LegacyLook>} />
              <Route path="/elevation/icon-library" element={<LegacyLook><DeprecatedBanner to="/elevation/design-language" label="The design language" /><IconLibrary /></LegacyLook>} />
              <Route path="/elevation/brand-elements" element={<LegacyLook><DeprecatedBanner to="/elevation/design-language" label="The design language" /><BrandElements /></LegacyLook>} />
              <Route path="/elevation/component-library" element={<LegacyLook><DeprecatedBanner to="/elevation/design-system/components" label="Component catalog" /><ComponentLibrary /></LegacyLook>} />
              <Route path="/elevation/icon-customizer" element={<IconCustomizer />} />
              <Route path="/elevation/table-designer" element={<TableDesigner />} />
              {/* Legacy design pages (Proposal 09 § 8.2: kept under a Legacy heading) */}
              <Route path="/elevation/archive/design-language-legacy" element={<DesignLanguageLegacy />} />
              <Route path="/elevation/comprehensive-visual-identity-system" element={<LegacyLook><DeprecatedBanner to="/elevation/design-language" label="The design language" /><ComprehensiveVisualIdentitySystem /></LegacyLook>} />
              {/* Placed in Design Language by Wes (Proposal 09 § 8.2) */}
              <Route path="/elevation/lab/brand-guidelines-document" element={<BrandGuidelinesDocument />} />
              <Route path="/elevation/lab/metaphor-studio" element={<MetaphorStudio />} />
              <Route path="/elevation/lab/visual-verbal-integration" element={<VisualVerbalIntegration />} />
              <Route path="/elevation/lab/geometry-studio" element={<GeometryStudio />} />
            </Route> {/* end DesignLanguageShell */}
                      <Route path="*" element={<LegacyElevationRedirect />} />
            </Routes>
          </ConditionalMain>
          <ConditionalFooter />
            {/* Learning System Components */}
            {/* <WelcomeModal /> */}
            <AchievementNotificationManager />
            {/* Elevation's own search (Cmd/Ctrl+K): the owner's, on his pages, loaded on demand */}
            <ElevationSearch isOpen={isSearchOpen} onOpenChange={setIsSearchOpen} />
            {/* Overlay Wizard */}
            <OverlayWizard />
            {/* Teaching Wizard */}
            <TeachingWizard 
              isOpen={isTeachingWizardOpen}
              onClose={() => setIsTeachingWizardOpen(false)}
              onComplete={() => setIsTeachingWizardOpen(false)}
            />
            {/* Product Tour: walks the owner's own pages, so it opens only for the owner */}
            <ProductTour
              isOpen={isProductTourOpen && elevationOwner}
              onClose={() => setIsProductTourOpen(false)}
              onComplete={() => setIsProductTourOpen(false)}
            />
          </AppElevationBody>
        </RequireAuth>
      </WizardProvider>
    </Router>
  );
}

export default App;